import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, PhoneIcon, VideoIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import GoogleMeetLogo from 'images/logos/google-meet.svg';
import GoToMeetingLogo from 'images/logos/gotomeeting.svg';
import MsTeamsLogo from 'images/logos/ms-teams.svg';
import WebexLogo from 'images/logos/webex.svg';
import ZoomLogo from 'images/logos/zoom.svg';
const locationContainerFlexStartCss = "l1h64lbn";
const locationContainerCss = "l1jzdg9f";
const LocationContainer = forwardRef((props, ref) => {
  const {
    flexStart,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(locationContainerCss, {
    [locationContainerFlexStartCss]: flexStart
  }, props.className)} />;
});
const nameAndChevronRowCss = "nouaw3f";
const chevronButtonCss = "ckiisux";
const chevronIconCss = "c1og8mex";
const joinMeetingButtonCss = "jqmkf0j";
const JoinMeetingButton = forwardRef((props, ref) => <Button ref={ref} data-testid="video-conf-join-meeting-button" {...props} className={classNames(joinMeetingButtonCss, props.className)} />);
const detailsContainerCss = "d1q0voaj";
const DetailsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(detailsContainerCss, props.className)} />;
});
const hostLabelCss = "h1nzt81r";
const HostLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(hostLabelCss, props.className)} />;
});
const detailsTextCss = "dwkhv59";
const DetailsText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(detailsTextCss, props.className)} />;
});
const textBlockCss = "t1hitv2a";
const TextBlock = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textBlockCss, props.className)} />;
});
const PHONE_LOCATION_KINDS = ['inbound_call', 'outbound_call'];
const styledImgCss = "slh9zem";
const iconWrapperCss = "isq24x4";
const StyledImg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  return <Component ref={ref} {...restProps} className={classNames(styledImgCss, props.className)} />;
});
const containerCss = "cccv5jl";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
export const VideoConferencingDetails = ({
  location,
  hosts,
  cancelled,
  past
}) => {
  const {
    kind,
    name,
    joinUrl,
    oneTapMobile,
    phoneNumber,
    formattedMeetingId,
    intlNumbersUrl,
    externalLocationPassword
  } = location;
  const {
    data: currentUser
  } = useGetUserQuery();
  const [detailsOpened, openDetails, closeDetails] = useBooleanState(false);
  const iconMap = {
    google_conference: GoogleMeetLogo,
    google_meet: GoogleMeetLogo,
    gotomeeting: GoToMeetingLogo,
    microsoft_teams: MsTeamsLogo,
    microsoft_teams_conference: MsTeamsLogo,
    webex_conference: WebexLogo,
    zoom: ZoomLogo
  };
  const hasDetails = !cancelled && Boolean(hosts && hosts.length);
  const isPhoneKind = PHONE_LOCATION_KINDS.includes(kind);
  const logoSrc = iconMap[kind];
  return <Container>
      <LocationContainer flexStart={cancelled || past || !joinUrl}>
        {isPhoneKind ? <span className={classNames(styledImgCss, iconWrapperCss)} aria-hidden>
            <PhoneIcon size="x-large" />
          </span> : <StyledImg src={logoSrc} alt={kind} />}
        <div className={nameAndChevronRowCss}>
          <BodyText weight="semibold" color={colorTextStandard}>{name}</BodyText>
          {hasDetails && <Link as="button" underline={false} onClick={detailsOpened ? closeDetails : openDetails} data-testid="video-conf-view-details" aria-label={I18n.t('publisher.meetings.location.view_details')} className={chevronButtonCss}>
              <span className={chevronIconCss}>
                {detailsOpened ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
              </span>
            </Link>}
        </div>
        {!cancelled && !past && !!joinUrl && <JoinMeetingButton as="a" size="small" decoration="secondary-outline" href={joinUrl} responsive={false}>
            {I18n.t('publisher.meetings.location.join_meeting')}
          </JoinMeetingButton>}
      </LocationContainer>
      <DetailsContainer data-testid="video-conf-details">
        {detailsOpened && <>
            {hosts.map(obj => obj.uuid).includes(currentUser.uuid) && <HostLabel>
                  <VideoIcon />
                  {I18n.t('publisher.meetings.location.video_conferencing_host_text', {
            video_kind: name
          })}
                </HostLabel>}
            <DetailsText>
              <TextBlock>
                <BodyText size="medium" weight="bold">{I18n.t('publisher.meetings.location.video_conferencing_message')}</BodyText>
                <Link href={joinUrl} underline={false}>{joinUrl}</Link>
              </TextBlock>
              {externalLocationPassword && <TextBlock>
                  <BodyText size="medium" weight="bold">
                    {`${I18n.t('publisher.meetings.location.password')}: `}
                    <BodyText size="medium" inline>
                      <FullStoryMask>{externalLocationPassword}</FullStoryMask>
                    </BodyText>
                  </BodyText>
                </TextBlock>}
              <TextBlock>
                <BodyText size="medium" weight="bold">{I18n.t('publisher.meetings.location.one_tap')}</BodyText>
                {(oneTapMobile || []).map(number => <Link href={`tel:${number}`} underline={false} key={number}>{number}</Link>)}
              </TextBlock>
              <TextBlock>
                <BodyText size="medium">{I18n.t('publisher.meetings.location.dial_in_message')}</BodyText>
                <BodyText size="medium"><FullStoryMask>{phoneNumber}</FullStoryMask></BodyText>
              </TextBlock>
              <BodyText size="medium">
                <FullStoryMask>{`${I18n.t('publisher.meetings.location.meeting_id')}: ${formattedMeetingId}`}</FullStoryMask>
              </BodyText>
              <BodyText size="medium">
                {`${I18n.t('publisher.meetings.location.find_local_number')}: `}
                <FullStoryMask>
                  <Link href={intlNumbersUrl} underline={false}>{intlNumbersUrl}</Link>
                </FullStoryMask>
              </BodyText>
            </DetailsText>
          </>}
      </DetailsContainer>
    </Container>;
};

require("./video_conferencing_details.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./video_conferencing_details.js");